import femaleClothTypesData from '../ai-designer-configs/cloth-types-female.json';
import maleClothTypesData from '../ai-designer-configs/cloth-types-male.json';

export type Gender = 'male' | 'female';
export type GenderSetting = Gender | 'auto';

export interface AttributeGroup {
  key: string;
  label: string;
  options: string[];
}

export interface ClothTypeDef {
  id: string;
  name: string;
  category: string;
  gender: Gender;
  attributes: AttributeGroup[];
}

interface RawAttributeGroup {
  label: string;
  options: string[];
}

interface RawClothType {
  id: string;
  name: string;
  category: string;
  attributes: Record<string, RawAttributeGroup>;
}

const rawByGender: Record<Gender, RawClothType[]> = {
  female: (femaleClothTypesData as unknown as { clothTypes: RawClothType[] }).clothTypes,
  male: (maleClothTypesData as unknown as { clothTypes: RawClothType[] }).clothTypes,
};

const buildClothTypes = (gender: Gender): ClothTypeDef[] =>
  rawByGender[gender].map((clothType) => ({
    id: clothType.id,
    name: clothType.name,
    category: clothType.category,
    gender,
    attributes: Object.entries(clothType.attributes).map(([key, group]) => ({
      key,
      label: group.label,
      options: group.options,
    })),
  }));

export const CLOTH_TYPES_BY_GENDER: Record<Gender, ClothTypeDef[]> = {
  female: buildClothTypes('female'),
  male: buildClothTypes('male'),
};

export const getClothTypesForGender = (gender: GenderSetting): ClothTypeDef[] => {
  if (gender !== 'auto') {
    return CLOTH_TYPES_BY_GENDER[gender];
  }

  const merged = new Map<string, ClothTypeDef>();
  [...CLOTH_TYPES_BY_GENDER.female, ...CLOTH_TYPES_BY_GENDER.male].forEach((clothType) => {
    if (!merged.has(clothType.id)) {
      merged.set(clothType.id, clothType);
    }
  });

  return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export const getClothType = (id: string, gender: GenderSetting): ClothTypeDef | undefined =>
  getClothTypesForGender(gender).find((clothType) => clothType.id === id);

export const snakeCase = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const attributesToPayload = (
  attributes: Record<string, string[]>
): Record<string, string[]> => {
  const payload: Record<string, string[]> = {};
  Object.entries(attributes).forEach(([label, values]) => {
    if (values.length > 0) {
      payload[snakeCase(label)] = values;
    }
  });
  return payload;
};

export const countDesignCombinations = (attributes: Record<string, string[]>): number => {
  const counts = Object.values(attributes).filter((values) => values.length > 0);
  if (counts.length === 0) return 0;
  return counts.reduce((total, values) => total * values.length, 1);
};
